import React, { useEffect, useState, Fragment } from 'react';
import { motion } from 'framer-motion';
import { PlusIcon, XIcon, UserIcon } from 'lucide-react';
interface PlayerEntryProps {
  gameMode: string;
  onComplete: (players: string[]) => void;
}
export function PlayerEntry({
  gameMode,
  onComplete
}: PlayerEntryProps) {
  // Initialize players based on mode
  const [players, setPlayers] = useState<string[]>(() => {
    if (gameMode === 'match') return ['', ''];
    if (gameMode === 'bestball') return ['', '', '', ''];
    return [''];
  });
  // Enforce player counts based on mode
  useEffect(() => {
    if (gameMode === 'match' && players.length !== 2) {
      setPlayers(['', '']);
    } else if (gameMode === 'bestball' && players.length !== 4) {
      setPlayers(['', '', '', '']);
    }
  }, [gameMode]);
  const addPlayer = () => {
    if (players.length < 4 && gameMode !== 'match' && gameMode !== 'bestball') {
      setPlayers([...players, '']);
    }
  };
  const removePlayer = (index: number) => {
    if (gameMode === 'match' || gameMode === 'bestball') return;
    const newPlayers = [...players];
    newPlayers.splice(index, 1);
    setPlayers(newPlayers);
  };
  const updatePlayer = (index: number, value: string) => {
    const newPlayers = [...players];
    newPlayers[index] = value;
    setPlayers(newPlayers);
  };
  const handleContinue = () => {
    const validPlayers = players.filter(p => p.trim() !== '');
    // For match and bestball, require all fields to be filled
    if (gameMode === 'match' && validPlayers.length !== 2) return;
    if (gameMode === 'bestball' && validPlayers.length !== 4) return;
    if (validPlayers.length > 0) {
      onComplete(validPlayers);
    }
  };
  const isValid = () => {
    const validCount = players.filter(p => p.trim() !== '').length;
    if (gameMode === 'match') return validCount === 2;
    if (gameMode === 'bestball') return validCount === 4;
    return validCount > 0;
  };
  const getTitle = () => {
    if (gameMode === 'match') return 'The Competitors';
    if (gameMode === 'bestball') return 'Build Your Teams';
    if (gameMode === 'challenge') return 'The Challengers';
    return "Who's Playing?";
  };
  const getSubtitle = () => {
    if (gameMode === 'match') return 'Enter 2 players for head-to-head match play.';
    if (gameMode === 'bestball') return 'Enter 4 players to form 2 teams.';
    return 'Add up to 4 players for this round.';
  };
  const getPlayerColor = (index: number) => {
    if (gameMode === 'bestball') {
      return index < 2 ? 'text-brand-gold' : 'text-brand-green-light';
    }
    const colors = ['text-brand-gold', 'text-brand-green-light', 'text-brand-cream-light', 'text-brand-sand'];
    return colors[index % colors.length];
  };
  return <div className="flex flex-col h-full p-6 bg-brand-charcoal relative" data-id="element-161">
      <div className="mt-8 mb-8" data-id="element-162">
        <h2 className="text-3xl font-heading text-brand-cream-light mb-2" data-id="element-163">
          {getTitle()}
        </h2>
        <p className="text-brand-cream/60 font-body text-sm" data-id="element-164">{getSubtitle()}</p>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto pb-4" data-id="element-165">
        {gameMode === 'bestball' && <div className="text-brand-gold text-xs font-bold uppercase tracking-widest mb-2 ml-2" data-id="element-166">
            Team 1
          </div>}

        {players.map((player, index) => <Fragment key={index} data-id="element-167">
            {gameMode === 'bestball' && index === 2 && <div className="text-brand-green-light text-xs font-bold uppercase tracking-widest mt-6 mb-2 ml-2" data-id="element-168">
                Team 2
              </div>}
            <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="relative" data-id="element-169">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-170">
                <UserIcon className={`h-5 w-5 ${getPlayerColor(index)}`} data-id="element-171" />
              </div>
              <input type="text" value={player} onChange={e => updatePlayer(index, e.target.value)} placeholder={`Player ${index + 1}`} className="w-full bg-brand-green-deep/50 border border-brand-cream/10 rounded-xl py-4 pl-12 pr-12 text-brand-cream-light font-body focus:outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-colors placeholder:text-brand-cream/30" autoFocus={index === 0} data-id="element-172" />
              {players.length > 1 && gameMode !== 'match' && gameMode !== 'bestball' && <button onClick={() => removePlayer(index)} className="absolute inset-y-0 right-0 pr-4 flex items-center text-brand-cream/30 hover:text-brand-cream-light transition-colors" data-id="element-173">
                    <XIcon className="h-5 w-5" data-id="element-174" />
                  </button>}
            </motion.div>
          </Fragment>)}

        {players.length < 4 && gameMode !== 'match' && gameMode !== 'bestball' && <button onClick={addPlayer} className="w-full py-4 border border-dashed border-brand-cream/20 rounded-xl text-brand-cream/50 font-body text-sm flex items-center justify-center space-x-2 hover:bg-brand-cream/5 hover:text-brand-cream-light hover:border-brand-cream/40 transition-all" data-id="element-175">
              <PlusIcon className="w-4 h-4" data-id="element-176" />
              <span data-id="element-177">Add Player</span>
            </button>}
      </div>

      <div className="mt-auto pt-6 pb-4" data-id="element-178">
        <button onClick={handleContinue} disabled={!isValid()} className={`w-full py-5 rounded-xl font-heading text-xl transition-all active:scale-[0.98] ${isValid() ? 'bg-brand-gold text-brand-charcoal shadow-lg hover:bg-brand-gold-light' : 'bg-brand-cream/10 text-brand-cream/30 cursor-not-allowed'}`} data-id="element-179">
          Let's Go
        </button>
      </div>
    </div>;
}